/* RK Gap report form: POST { store_url, email, consent, utm, page_url, cf-turnstile-response }
   to <worker>/gap-report. The Worker answers { ok, status: 'queued' | 'ready' } or
   { ok: false, error, message } with a friendly message to show as is. */
(() => {
  const UTM_KEY = 'rk-gap-utm';
  const REQUEST_TIMEOUT_MS = 45000;

  // Outreach links may prefill a public store domain. Never prefill an email,
  // tick consent, or submit: the visitor still asks for their own report.
  function outreachStore() {
    const value = new URLSearchParams(window.location.search).get('store_url');
    if (!value || value.length > 300) return '';
    try {
      const url = new URL(/^https?:\/\//i.test(value) ? value : `https://${value}`);
      const host = url.hostname.toLowerCase();
      if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.port) return '';
      if (!/^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(host) || /^\d+(\.\d+){3}$/.test(host) || /(^|\.)(localhost|local|internal|workers\.dev)$/.test(host)) return '';
      return host;
    } catch {
      return '';
    }
  }

  function utm() {
    const q = new URLSearchParams(window.location.search);
    const fresh = {};
    for (const k of ['source', 'medium', 'campaign', 'content', 'term']) {
      const v = q.get(`utm_${k}`);
      if (v) fresh[k] = v.slice(0, 60);
    }
    try {
      if (Object.keys(fresh).length) sessionStorage.setItem(UTM_KEY, JSON.stringify(fresh));
      return Object.keys(fresh).length ? fresh : JSON.parse(sessionStorage.getItem(UTM_KEY) || '{}');
    } catch {
      return fresh;
    }
  }

  function init(form) {
    if (form.dataset.ready) return;
    form.dataset.ready = '1';
    const wrap = form.closest('.rk-gr__signup');
    const msg = form.querySelector('.rk-gr__message');
    const btn = form.querySelector('.rk-gr__submit');
    const done = wrap.querySelector('.rk-gr__done');
    const endpoint = `${String(form.dataset.workerUrl || '').replace(/\/$/, '')}/gap-report`;
    utm();
    if (!form.elements.store_url.value) form.elements.store_url.value = outreachStore();
    const resetChallenge = () => {
      if (window.turnstile && form.querySelector('.cf-turnstile')) {
        window.turnstile.reset(form.querySelector('.cf-turnstile'));
      }
    };

    const show = (text) => {
      msg.textContent = text;
      msg.hidden = !text;
      msg.dataset.state = 'error';
    };
    const mark = (name, bad) => form.elements[name] && form.elements[name].setAttribute('aria-invalid', bad ? 'true' : 'false');

    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      if (btn.disabled) return;
      show('');
      const store = form.elements.store_url.value.trim();
      const email = form.elements.email.value.trim();
      const consent = form.elements.consent.checked;
      mark('store_url', !store);
      mark('email', !/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email));
      if (!store) return show("Add your store's web address.");
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)) return show("That email doesn't look right.");
      if (!consent) return show('Please tick the box so we can email you the report.');
      const tokenField = form.querySelector('[name="cf-turnstile-response"]');
      if (form.querySelector('.cf-turnstile') && !(tokenField && tokenField.value)) return show('One moment: the spam check is still loading. Try again in a second.');

      btn.disabled = true;
      const label = btn.textContent;
      btn.textContent = 'Building your report…';
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
      try {
        const res = await fetch(endpoint, {
          method: 'POST',
          signal: controller.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            store_url: store,
            email,
            consent: true,
            website: form.elements.website ? form.elements.website.value : '',
            utm: utm(),
            page_url: window.location.href,
            'cf-turnstile-response': tokenField ? tokenField.value : '',
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || !data.ok || !['queued', 'ready'].includes(data.status)) {
          show(data.message || 'Something went wrong on our side. Please try again in a minute.');
          if (data.error === 'invalid_store' || data.error === 'not_shopify') mark('store_url', true);
          resetChallenge();
          return;
        }
        done.querySelector('.rk-gr__done-body').textContent = data.status === 'ready' ? form.dataset.readyBody : form.dataset.doneBody;
        form.hidden = true;
        done.hidden = false;
        done.focus();
        if (window.umami && typeof window.umami.track === 'function') window.umami.track('gap-report-requested', { status: data.status });
        if (window.posthog) {
          // Same 'shop' group key the Recipe Kit app uses, so a later install joins this visit.
          if (data.shop_domain) window.posthog.group('shop', data.shop_domain);
          window.posthog.capture('gap_report_requested', { status: data.status, has_shop_domain: Boolean(data.shop_domain) });
        }
      } catch (error) {
        show(error.name === 'AbortError'
          ? 'The request took too long. Please try again in a minute.'
          : "We couldn't reach our server. Check your connection and try again.");
        resetChallenge();
      } finally {
        clearTimeout(timeout);
        btn.disabled = false;
        btn.textContent = label;
      }
    });
  }

  const boot = () => document.querySelectorAll('form[data-rk-gap-report]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
